import { deepCloneArray } from '../../utils/array';
import { getId } from '../../utils/item';

export const createBoardDrawEntityId = () =>
  typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `${Date.now()}-${String(Math.random()).slice(2)}`;

export const createShapeFromDraft = draft => {
  if (
    !draft ||
    !Number.isFinite(draft?.w) ||
    !Number.isFinite(draft?.h) ||
    draft.w < 8 ||
    draft.h < 8
  ) {
    return null;
  }

  return {
    id: createBoardDrawEntityId(),
    type: ['rect', 'diamond', 'triangle', 'star'].includes(draft.type)
      ? draft.type
      : 'circle',
    x: draft.x,
    y: draft.y,
    w: draft.w,
    h: draft.h,
    stroke: 'secondary',
    strokeWidth: 2,
    fillOpacity: 0.25,
    layer: 'back',
  };
};

export const createFreeDrawShapeFromPoints = points => {
  const nextPoints = Array.isArray(points)
    ? points.filter(
        point =>
          Number.isFinite(point?.x) &&
          Number.isFinite(point?.y),
      )
    : [];

  if (nextPoints.length < 2) {
    return null;
  }

  const xs = nextPoints.map(point => point.x);
  const ys = nextPoints.map(point => point.y);
  const minX = Math.min(...xs);
  const minY = Math.min(...ys);
  const maxX = Math.max(...xs);
  const maxY = Math.max(...ys);

  return {
    id: createBoardDrawEntityId(),
    type: 'path',
    x: minX,
    y: minY,
    w: Math.max(1, maxX - minX),
    h: Math.max(1, maxY - minY),
    points: nextPoints.map(point => ({
      x: point.x,
      y: point.y,
    })),
    stroke: 'secondary',
    strokeWidth: 2,
    strokeDasharray: null,
    fillOpacity: 0,
    layer: 'back',
  };
};

export const extendGroupsForNodeRect = ({ groups, activeGroupId, rect }) => {
  if (!activeGroupId || !rect) {
    return groups;
  }
  const maxX = (Number(rect?.x) || 0) + (Number(rect?.w) || 0);
  const maxY = (Number(rect?.y) || 0) + (Number(rect?.h) || 0);
  return groups.map(group => {
    if (group?.id !== activeGroupId) {
      return group;
    }
    return {
      ...group,
      w: Math.max(Number(group?.w) || 0, maxX),
      h: Math.max(Number(group?.h) || 0, maxY),
    };
  });
};

export const attachNodeToGroups = ({
  groups,
  activeGroupId,
  nodeId,
  rect,
}) => {
  if (!activeGroupId || !nodeId) {
    return groups;
  }

  const nextGroupsBase = groups.map(group => {
    const childIds = Array.isArray(group?.childIds) ? group.childIds : [];
    if (group?.id === activeGroupId) {
      return childIds.includes(nodeId)
        ? group
        : { ...group, childIds: [...childIds, nodeId] };
    }
    if (childIds.includes(nodeId)) {
      return {
        ...group,
        childIds: childIds.filter(entry => entry !== nodeId),
      };
    }
    return group;
  });

  return extendGroupsForNodeRect({
    groups: nextGroupsBase,
    activeGroupId,
    rect,
  });
};

export const upsertBoardWidgetInGrid = ({
  grid,
  droppedItemId,
  boardMetrics,
  fallbackParentItemId,
  nextGridX,
  nextGridY,
  defaultW = 4,
  defaultH = 2,
}) => {
  const nextGrid = deepCloneArray(Array.isArray(grid) ? grid : []);
  let rect = null;

  const existingIdx = nextGrid.findIndex(gridObj => getId(gridObj) === droppedItemId);
  if (existingIdx > -1) {
    const existing = nextGrid[existingIdx]?.[droppedItemId];
    if (existing) {
      existing.x = nextGridX;
      existing.y = nextGridY;
      rect = {
        x: nextGridX * boardMetrics.colWidth,
        y: nextGridY * boardMetrics.rowHeight,
        w: (Number(existing?.w) || 0) * boardMetrics.colWidth,
        h: (Number(existing?.h) || 0) * boardMetrics.rowHeight,
      };
    }
    return {
      nextGrid,
      rect,
      existed: true,
    };
  }

  nextGrid.push({
    [droppedItemId]: {
      item: fallbackParentItemId,
      type: 'link',
      displayType: 'link',
      x: nextGridX,
      y: nextGridY,
      w: defaultW,
      h: defaultH,
      i: droppedItemId,
    },
  });

  rect = {
    x: nextGridX * boardMetrics.colWidth,
    y: nextGridY * boardMetrics.rowHeight,
    w: defaultW * boardMetrics.colWidth,
    h: defaultH * boardMetrics.rowHeight,
  };

  return {
    nextGrid,
    rect,
    existed: false,
  };
};
